import React, { useEffect, useMemo, useState } from 'react';
import MarketplaceShell from './MarketplaceShell';
import MarketplaceErrorScreen from './MarketplaceErrorScreen';
import { MktCard, Pill, Eyebrow, Skeleton } from './marketplaceUi';
import {
  getSubCategories,
  getArchetypes,
  type SubCategory,
  type Archetype,
} from './marketplaceApi';
import { profileDescription } from './profileDescriptions';

const ALL = '__all__';

/** Below this the flat 34-card grid becomes an unusable single-column scroll, so the
 *  catalogue is rendered as collapsible per-category sections instead. Matches Tailwind's `md`. */
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

/**
 * Shown only for a profile the research sheet has no description for. Every profile
 * without real copy gets the SAME honest placeholder rather than a fabricated
 * personality — filler that reads like research is worse than filler that admits it is.
 */
const PLACEHOLDER_BLURB =
  'A group of AI Consumer Twins modeled on real shoppers who share these priorities. You select ' +
  'the Segment whole — individual Twins aren’t broken out.';

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_MEDIA_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const ExploreTwins: React.FC = () => {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [byCat, setByCat] = useState<Record<string, Archetype[]>>({});
  const [active, setActive] = useState<string>(ALL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getSubCategories()
      .then(async (s) => {
        if (!alive) return;
        setSubs(s);
        // Every category's archetypes, in parallel. One failing category degrades to
        // an empty list instead of blanking the whole page.
        const lists = await Promise.all(
          s.map((sc) => getArchetypes(sc.slug).catch(() => [] as Archetype[])),
        );
        if (!alive) return;
        setByCat(Object.fromEntries(s.map((sc, i) => [sc.slug, lists[i]])));
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const visible = useMemo<Archetype[]>(() => {
    const inCategory =
      active !== ALL ? byCat[active] || [] : subs.flatMap((sc) => byCat[sc.slug] || []);
    const q = query.trim().toLowerCase();
    if (!q) return inCategory;
    return inCategory.filter(
      (a) => a.name.toLowerCase().includes(q) || a.sub_category_name.toLowerCase().includes(q),
    );
  }, [active, byCat, subs, query]);

  const grouped = useMemo(
    () =>
      subs
        .map((sc) => ({ sc, items: visible.filter((a) => a.sub_category === sc.slug) }))
        .filter((g) => g.items.length > 0),
    [subs, visible],
  );

  // A narrowed list is short enough to show outright; only the unfiltered catalogue
  // needs collapsing, so searching or picking a category force-opens every section.
  const narrowed = active !== ALL || query.trim() !== '';
  const isOpen = (slug: string) => narrowed || !!openCats[slug];
  const toggleCat = (slug: string) => setOpenCats((prev) => ({ ...prev, [slug]: !prev[slug] }));

  const totalCount = useMemo(
    () => subs.reduce((n, sc) => n + (byCat[sc.slug]?.length || 0), 0),
    [subs, byCat],
  );

  const renderCard = (a: Archetype) => (
    <MktCard key={a.archetype_id} className="flex flex-col p-5">
      <div className="text-[14px] font-semibold text-[#111]">{a.name}</div>
      <div className="mt-0.5 text-[11px] text-[#9ca3af]">{a.sub_category_name} · Segment</div>
      <p className="mt-3 text-[12px] leading-relaxed text-[#6b7280]">
        {profileDescription(a.sub_category_name, a.name) ?? PLACEHOLDER_BLURB}
      </p>
      <div className="mt-auto flex items-center justify-between pt-4">
        <span className="rounded-md bg-[#E8FCFA] px-2 py-1 text-[11px] font-medium text-[#0B7C74]">
          {a.twin_count} Twins
        </span>
      </div>
    </MktCard>
  );

  if (error) {
    return (
      <MarketplaceErrorScreen
        title="We couldn’t load the Segments"
        message="Something went wrong fetching the Twin catalog. Please try again."
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <MarketplaceShell maxWidth="max-w-5xl">
      <Eyebrow>Explore Segments</Eyebrow>
      <h1 className="mt-1 text-[26px] font-semibold text-[#111]">
        Meet your AI Consumer Twin Segments
      </h1>
      <p className="mt-1.5 text-[13px] text-[#6b7280]">
        Browse the Segments behind every study — filter by Breakfast Category, then run a study on
        the ones you want.
      </p>

      {/* Category filter */}
      <div className="-mx-4 mt-5 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
        <Pill active={active === ALL} onClick={() => setActive(ALL)}>
          All Segments {totalCount > 0 && <span className="ml-1 opacity-70">{totalCount}</span>}
        </Pill>
        {subs.map((sc) => (
          <Pill key={sc.slug} active={active === sc.slug} onClick={() => setActive(sc.slug)}>
            {sc.name}{' '}
            <span className="ml-1 opacity-70">{byCat[sc.slug]?.length ?? sc.archetype_count}</span>
          </Pill>
        ))}
      </div>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search Segments…"
        aria-label="Search Segments"
        className="mt-3 h-11 w-full rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111] placeholder:text-[#9ca3af] focus:border-primary focus:outline-none md:hidden"
      />

      <p className="mt-3 text-[12px] text-[#9ca3af]">
        {loading ? 'Loading Segments…' : `${visible.length} shown`} · select a Segment whole, not
        individual Twins
      </p>

      {loading ? (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <MktCard key={i} className="p-5">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/3" />
              <Skeleton className="mt-4 h-3 w-full" />
              <Skeleton className="mt-1.5 h-3 w-5/6" />
              <Skeleton className="mt-4 h-6 w-24 rounded-full" />
            </MktCard>
          ))}
        </div>
      ) : isMobile ? (
        <div className="mt-4 space-y-2">
          {grouped.map(({ sc, items }) => (
            <div key={sc.slug}>
              <button
                onClick={() => toggleCat(sc.slug)}
                aria-expanded={isOpen(sc.slug)}
                className="flex min-h-[44px] w-full items-center justify-between rounded-lg border border-[#e5e7eb] bg-white px-3 py-2 text-left"
              >
                <span className="text-[13px] font-semibold text-[#111]">{sc.name}</span>
                <span className="flex items-center gap-2 text-[12px] text-[#9ca3af]">
                  {items.length}
                  <span aria-hidden className={isOpen(sc.slug) ? 'rotate-180' : ''}>
                    ⌄
                  </span>
                </span>
              </button>
              {isOpen(sc.slug) && (
                <div className="mt-2 grid grid-cols-1 gap-3">{items.map(renderCard)}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map(renderCard)}
        </div>
      )}

      {!loading && visible.length === 0 && (
        <MktCard className="mt-4 p-8 text-center">
          <p className="text-[13px] text-[#6b7280]">
            {query.trim() ? 'No Segments match that search.' : 'No Segments in this category yet.'}
          </p>
        </MktCard>
      )}
    </MarketplaceShell>
  );
};

export default ExploreTwins;
